import styled, { useTheme } from 'styled-components';
import { Heading4Text } from '../../components/Texts';
import TextInput from '../../components/Inputs/Text';
import { useEffect } from 'react';
import { PHONE_MEDIA_QUERY, useMediaQuery } from '../../common/responsive';
import { useNavigate } from 'react-router-dom';
import { formatUSDEnsure2Digits } from '../../common/currency';
import Button from '../../components/Button';
import ScrollRestore from '../../components/ScrollRestore';
import { useInView } from 'react-intersection-observer';
import { useTokenSearch } from '../../hooks/useTokenSearch';
import { Table, TableContainer, Tbody, Td, Tr } from '../../components/Table';
import { truncateAssetName } from '../../common/tokens';
import TableSkeleton from '../../components/Table/TableSkeleton';
import { hapticsImpactLight } from '../../common/haptic';
import { IonRefresher, IonRefresherContent } from '@ionic/react';
import { useSolana } from '../../hooks/useSolana';
import ChevronLeftIcon from '../../assets/icons/chevron-left.svg?react';

const Container = styled.div`
  padding-top: 8px;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: flex-start;
  width: 100%;
  gap: 16px;
  @media ${PHONE_MEDIA_QUERY} {
    gap: 8px;
  }
`;

const StyledHeading4Text = styled(Heading4Text)`
  @media ${PHONE_MEDIA_QUERY} {
    font-size: 24px;
  }
`;

const TitleAndSearchContainer = styled.div`
  display: flex;
  width: 100%;
  align-items: center;
`;

const InnerContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  width: 100%;
  @media ${PHONE_MEDIA_QUERY} {
    gap: 8px;
  }
`;

const CategoryContainer = styled.div`
  padding: 12px 16px;
  display: flex;
  gap: 6px;
  justify-content: center;
  align-items: center;
  border-radius: 999px;
  color: ${({ theme }) => theme.text.regular};
  border: 1px solid ${({ theme }) => theme.border.primary};
  background-color: ${({ theme }) => theme.background.primary};
  font-size: 14px;
  cursor: pointer;
  width: 100%;
  font-weight: 600;
  width: fit-content;
  white-space: nowrap;
  ${({ selected, theme }) =>
    selected &&
    `
    border: 1px solid transparent;
    color: ${theme.primary.primary};
    background-color: ${theme.primary.background};
  `};
  @media ${PHONE_MEDIA_QUERY} {
    padding: 8px 8px;
    font-size: 12px;
  }
`;

const CategoryContainerInner = styled.div`
  display: flex;
  padding-top: 5px;
  gap: 16px;
  width: fit-content;
  overflow: auto;
  scrollbar-width: none;
  -ms-overflow-style: none;
  &::-webkit-scrollbar {
    display: none;
  }
  @media ${PHONE_MEDIA_QUERY} {
    gap: 8px;
  }
`;

const CategoriesContainer = styled.div`
  display: flex;
  gap: 8px;
  overflow-x: scroll;
  &::-webkit-scrollbar {
    display: none;
  }
  scrollbar-width: none;
  -ms-overflow-style: none;
`;

const CATEGORIES = [
  'ALL',
  'POPULAR',
  'STOCKS',
  'PRESTOCKS',
  'DEFI',
  'MEME',
];

const CATEGORY_ICON = {
  ALL: <img src='/burguer.webp' width={16} height={16} />,
  POPULAR: '🚀',
  STOCKS: '📈',
  PRESTOCKS: '💎',
  DEFI: '💰',
  MEME: '🐶',
};

const AssetInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const Icon = styled.img`
  border-radius: 50%;
  object-fit: cover;
  width: 40px;
`;

const Label = styled.div`
  display: flex;
  flex-direction: column;
`;

const AssetName = styled.span`
  font-weight: 600;
  color: ${({ theme }) => theme.text.title};
`;

const AssetCode = styled.span`
  font-size: 12px;
  color: ${({ theme }) => theme.text.lighter};
`;

const TokenCategoriesContainer = styled.div`
  display: flex;
  gap: 6px;
`;

const TokenCategory = styled.div`
  text-transform: capitalize;
  font-size: 12px;
  padding: 4px 10px;
  color: rgba(199, 201, 204, 1);
  border: 1px solid rgba(199, 201, 204, 1);
  border-radius: 8px;
`;

const ButtonContainer = styled.div`
  display: flex;
  width: 100%;
  justify-content: flex-end;
  gap: 8px;
`;

const Row = ({ asset, refData }) => {
  const isMobile = useMediaQuery(PHONE_MEDIA_QUERY);
  const navigate = useNavigate();
  const theme = useTheme();

  return (
    <Tr
      ref={refData}
      key={asset.symbol}
      onClick={() => {
        hapticsImpactLight();
        navigate(`/app/tokens/${asset.address}?back=/app/home`);
      }}
    >
      <Td>
        <AssetInfo>
          <Icon src={asset.logoURI} />
          <Label>
            <AssetName>
              {isMobile ? truncateAssetName(asset.name) : asset.name}
            </AssetName>
            <AssetCode>{asset.symbol}</AssetCode>
          </Label>
        </AssetInfo>
      </Td>
        <Td>
          <div>
            <p style={{
              color: theme.text.title,
              ...(isMobile && {
                textAlign: 'right',
                fontWeight: 400,
                marginRight: '10px',
                fontSize: '16px',
              }),
            }}>
              {formatUSDEnsure2Digits(asset.usdPrice)}
            </p>
          </div>
        </Td>
      {!isMobile && (
        <>
          <Td>
            <TokenCategoriesContainer>
              {asset.categories.map((tokenCategory) => {
                return tokenCategory.name === 'TRENDING' ? (
                  <></>
                ) : (
                  <TokenCategory>
                    {tokenCategory.name.toLocaleLowerCase()}
                  </TokenCategory>
                );
              })}
            </TokenCategoriesContainer>
          </Td>
          <Td>
            <ButtonContainer>
              <Button
                onClick={(e) => {
                  e.stopPropagation();
                  hapticsImpactLight();
                  navigate(
                    `/app/tokens/${asset.address}?tab=trade&back=/app/home`,
                  );
                }}
                style={{ height: 32 }}
                variant='secondary'
              >
                TRADE
              </Button>
              <Button
                onClick={(e) => {
                  e.stopPropagation();
                  hapticsImpactLight();
                  navigate(
                    `/app/tokens/${asset.address}?tab=gift&back=/app/home`,
                  );
                }}
                variant='secondary'
                style={{ height: 32 }}
              >
                GIFT
              </Button>
            </ButtonContainer>
          </Td>
        </>
      )}
    </Tr>
  );
};

const Discovery = () => {
  const isMobile = useMediaQuery(PHONE_MEDIA_QUERY);
  const theme = useTheme();
  // Use the custom hook for token search and filtering
  const {
    searchTerm,
    selectedCategory,
    displayTokens,
    isLoading,
    isFetching,
    hasMore,
    setSelectedCategory,
    setSearchTerm,
    loadMore,
  } = useTokenSearch();

  const { ref, inView } = useInView({ threshold: 1 });

  const navigate = useNavigate();

  const { refreshTokenBalances } = useSolana();

  // Handle infinite scroll
  useEffect(() => {
    if (inView && hasMore && !isFetching) {
      loadMore();
    }
  }, [inView, hasMore, isFetching, loadMore]);

  const handleRefresh = async (event) => {
    await refreshTokenBalances();
    event.detail.complete();
  };

  return (
    <Container>
      <IonRefresher slot='fixed' onIonRefresh={handleRefresh}>
        <IonRefresherContent /> {/* self-closed; no page content inside */}
      </IonRefresher>
      {!isMobile && (
        <div
          style={{ display: 'flex', justifyContent: 'flex-start', width: '100%' }}
        >
          <ChevronLeftIcon width={9} height={48} color={theme.text.title} />
          <Button
            variant='link'
            onClick={() => navigate('/app/home')}
            style={{ color: theme.text.title }}
          >
            Back
          </Button>
        </div>
      )}
      <ScrollRestore />
      <StyledHeading4Text style={{ fontWeight: 600, color: theme.text.title, marginTop: '15px' }}>
        Discover assets
      </StyledHeading4Text>
      <InnerContainer>
        <TitleAndSearchContainer>
          <div style={{ minWidth: '100%' }}>
            <TextInput
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder='Search asset'
              icon={<img src='/search.webp' width={16} height={16} />}
            />
          </div>
        </TitleAndSearchContainer>
        <CategoriesContainer>
          <CategoryContainerInner>
            {CATEGORIES.map((category) => {
              return (
                <CategoryContainer
                  onClick={() => {
                    hapticsImpactLight();
                    setSelectedCategory(category);
                  }}
                  selected={category === selectedCategory}
                >
                  {CATEGORY_ICON[category]} {category}
                </CategoryContainer>
              );
            })}
          </CategoryContainerInner>
        </CategoriesContainer>
        <TableContainer>
          {isLoading ? (
            <TableSkeleton />
          ) : displayTokens.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '40px' }}>
              No tokens found matching your criteria.
            </div>
          ) : (
            <Table>
              <Tbody>
                {displayTokens.map((asset, i) => {
                  const isLast = i === displayTokens.length - 1;
                  return (
                    <Row
                      refData={isLast ? ref : null}
                      key={`${asset.address}-${i}`}
                      i={i}
                      asset={asset}
                    />
                  );
                })}
                {isFetching && (
                  <Tr>
                    <Td
                      colSpan={isMobile ? 3 : 5}
                      style={{ textAlign: 'center', padding: '20px' }}
                    >
                      Loading more tokens...
                    </Td>
                  </Tr>
                )}
              </Tbody>
            </Table>
          )}
        </TableContainer>
      </InnerContainer>
    </Container>
  );
};

export default Discovery;
